// ═══════════════════════════════════════════════════════════════════════════════
// ARTIFACT - Projet Interactif (pour Particuliers)
// Affiche et permet de gérer un projet de travaux
// ═══════════════════════════════════════════════════════════════════════════════

import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  FolderOpen, MapPin, Calendar, Euro, Clock, ChevronDown, ChevronUp,
  User, Phone, Mail, Star, Check, AlertCircle, Plus, Edit3, Trash2,
  MessageCircle, FileText, Camera, Send, Loader2
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ═══════════════════════════════════════════════════════════════════════════════
// DONNÉES DÉMO
// ═══════════════════════════════════════════════════════════════════════════════
export const DEMO_PROJECT = {
  id: 'project-demo-001',
  title: 'Rénovation salle de bain',
  description: 'Remplacement de la baignoire par une douche à l\'italienne, changement du carrelage et réfection de la plomberie.',
  status: 'en_cours', // nouveau, en_cours, devis_recus, en_travaux, termine
  category: 'Plomberie',
  location: {
    city: 'Lyon',
    postal_code: '69003'
  },
  budget: {
    min: 5000,
    max: 8000
  },
  urgency: 'normal', // urgent, normal, pas_presse
  created_at: '2026-04-15',
  photos: [],
  artisans_contacted: 3,
  devis_received: 2,
  messages_unread: 1
};

// ═══════════════════════════════════════════════════════════════════════════════
// COMPOSANT PRINCIPAL
// ═══════════════════════════════════════════════════════════════════════════════

export default function ArtifactProjetInteractif({ project: initialProject, onUpdate }) {
  const [project, setProject] = useState(initialProject || DEMO_PROJECT);
  const [isEditing, setIsEditing] = useState(false);
  const [expandedSection, setExpandedSection] = useState('details');
  const [isSaving, setIsSaving] = useState(false);
  
  // Status labels et couleurs
  const statusConfig = {
    nouveau: { label: 'Nouveau', color: 'bg-blue-100 text-blue-700', icon: Plus },
    en_cours: { label: 'Recherche artisans', color: 'bg-amber-100 text-amber-700', icon: Clock },
    devis_recus: { label: 'Devis reçus', color: 'bg-purple-100 text-purple-700', icon: FileText },
    en_travaux: { label: 'En travaux', color: 'bg-orange-100 text-orange-700', icon: AlertCircle },
    termine: { label: 'Terminé', color: 'bg-emerald-100 text-emerald-700', icon: Check }
  };
  
  const urgencyConfig = {
    urgent: { label: 'Urgent', color: 'text-red-600' },
    normal: { label: 'Normal', color: 'text-amber-600' },
    pas_presse: { label: 'Pas pressé', color: 'text-gray-500' }
  };
  
  const currentStatus = statusConfig[project.status] || statusConfig.nouveau;
  const currentUrgency = urgencyConfig[project.urgency] || urgencyConfig.normal;
  const StatusIcon = currentStatus.icon;
  
  // Toggle section
  const toggleSection = (section) => {
    setExpandedSection(expandedSection === section ? null : section);
  };
  
  // Sauvegarder les modifications
  const handleSave = async () => {
    setIsSaving(true);
    try {
      // Appel API pour sauvegarder
      // await fetch(`${API_URL}/api/projects/${project.id}`, ...)
      toast.success('Projet mis à jour');
      setIsEditing(false);
      if (onUpdate) onUpdate(project);
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setIsSaving(false);
    }
  };
  
  return (
    <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-sm">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#1E3A5F] to-[#2d5a8a] p-4 text-white">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
              <FolderOpen className="w-5 h-5" />
            </div>
            <div>
              <span className="text-xs text-white/70 uppercase tracking-wider">Projet</span>
              <h3 className="font-semibold">{project.title}</h3>
            </div>
          </div>
          <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${currentStatus.color}`}>
            {currentStatus.label}
          </span>
        </div>
        
        {/* Quick stats */}
        <div className="grid grid-cols-3 gap-2 mt-3">
          <div className="bg-white/10 rounded-lg p-2 text-center">
            <p className="text-2xl font-bold">{project.artisans_contacted}</p>
            <p className="text-xs text-white/70">Artisans contactés</p>
          </div>
          <div className="bg-white/10 rounded-lg p-2 text-center">
            <p className="text-2xl font-bold">{project.devis_received}</p>
            <p className="text-xs text-white/70">Devis reçus</p>
          </div>
          <div className="bg-white/10 rounded-lg p-2 text-center relative">
            <p className="text-2xl font-bold">{project.messages_unread}</p>
            <p className="text-xs text-white/70">Messages</p>
            {project.messages_unread > 0 && (
              <span className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full animate-pulse" />
            )}
          </div>
        </div>
      </div>
      
      {/* Description */}
      <div className="p-4 border-b border-gray-100">
        <p className="text-sm text-gray-600 leading-relaxed">{project.description}</p>
      </div>
      
      {/* Section Détails */}
      <div className="border-b border-gray-100">
        <button
          onClick={() => toggleSection('details')}
          className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
        >
          <span className="font-medium text-gray-900 text-sm">Détails du projet</span>
          {expandedSection === 'details' ? (
            <ChevronUp className="w-4 h-4 text-gray-400" />
          ) : (
            <ChevronDown className="w-4 h-4 text-gray-400" />
          )}
        </button>
        
        {expandedSection === 'details' && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="px-4 pb-4 space-y-3"
          >
            {/* Localisation */}
            <div className="flex items-center gap-3 text-sm">
              <MapPin className="w-4 h-4 text-gray-400" />
              <span className="text-gray-700">{project.location.postal_code} {project.location.city}</span>
            </div>
            
            {/* Catégorie */}
            <div className="flex items-center gap-3 text-sm">
              <FolderOpen className="w-4 h-4 text-gray-400" />
              <span className="text-gray-700">{project.category}</span>
            </div>
            
            {/* Budget */}
            <div className="flex items-center gap-3 text-sm">
              <Euro className="w-4 h-4 text-gray-400" />
              <span className="text-gray-700">
                Budget : {project.budget.min.toLocaleString()}€ - {project.budget.max.toLocaleString()}€
              </span>
            </div>
            
            {/* Urgence */}
            <div className="flex items-center gap-3 text-sm">
              <Clock className="w-4 h-4 text-gray-400" />
              <span className={currentUrgency.color}>{currentUrgency.label}</span>
            </div>
            
            {/* Date création */}
            <div className="flex items-center gap-3 text-sm">
              <Calendar className="w-4 h-4 text-gray-400" />
              <span className="text-gray-700">Créé le {new Date(project.created_at).toLocaleDateString('fr-FR')}</span>
            </div>
          </motion.div>
        )}
      </div>
      
      {/* Section Actions */}
      <div className="p-4 space-y-2">
        <button className="w-full flex items-center justify-center gap-2 py-2.5 bg-[#1E3A5F] text-white rounded-xl hover:bg-[#2d5a8a] transition-colors text-sm font-medium">
          <MessageCircle className="w-4 h-4" />
          Voir les messages
        </button>
        
        <div className="grid grid-cols-2 gap-2">
          <button className="flex items-center justify-center gap-2 py-2 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-colors text-sm">
            <FileText className="w-4 h-4" />
            Voir les devis
          </button>
          <button className="flex items-center justify-center gap-2 py-2 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-colors text-sm">
            <User className="w-4 h-4" />
            Artisans
          </button>
        </div>
        
        <button className="w-full flex items-center justify-center gap-2 py-2 border border-gray-200 text-gray-600 rounded-xl hover:bg-gray-50 transition-colors text-sm">
          <Edit3 className="w-4 h-4" />
          Modifier le projet
        </button>
      </div>
    </div>
  );
}
